/**
 * The page top bar's sharing indicator and the Share dialog (Notion-style): invite people, agents or — by email —
 * guests; "People with access" with their level and where it comes from; general access ("Everyone at <workspace>"
 * or only people invited); publish to the web with its options. Every rule comes from logic/sharing (the server runs
 * the same resolution); this class loads the dialog's state (SharingApi), applies the person's choices and follows
 * the page's events (any `page.updated` touching sharing refetches). Online only: failures show a notice.
 *
 * A move between sections (sidebar "Move to Private" / "Move to Workspace") shows at once: the tree's optimistic
 * visibility overrides the last fetched one (a private page without its own general access is "only people
 * invited"), and the state is refetched both when the tree changes and once more when the server's echo of the move
 * arrives (the first fetch can race the write). Refreshes never overlap: one asked for during a fetch runs after it.
 */
import type { GeneralAccess, InvitePageGuestRequest, PageAccess, PageKind, PageNode, PageSharing, PublicLink, PublishPageRequest } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import {
  DEFAULT_SETTINGS,
  expiryOptions,
  generalControl,
  inviteCandidates,
  isRestrictedSubject,
  organizationFromEmail,
  peopleRows,
  publishBlockedReason,
  shareIndicator,
  sharingCapabilities,
  type ExpiryOption,
  type GeneralControl,
  type InviteCandidate,
  type PersonRow,
  type ShareIndicator,
  type SharingCapabilities,
} from "../logic/sharing.ts";
import { ApiFailure, errorKind, type SharingApi } from "../ports.ts";
import type { PageTreeViewModel } from "./PageTreeViewModel.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type GeneralView = GeneralControl;

export type PublishView = {
  /** I may publish / change / revoke (full access + workspace policy). */
  available: boolean;
  /** Why not (policy off / admins only / not full access). */
  blockedReason?: MessageDescriptor;
  on: boolean;
  link?: PublicLink;
  expiry: ExpiryOption["key"];
  busy: boolean;
};

export type PageShareState = {
  status: "loading" | "ready" | "error";
  pageId: string;
  title: string;
  kind: PageKind;
  /** Top bar: "Shared with N" / Private / Workspace / Published. */
  indicator: ShareIndicator;
  caps: SharingCapabilities;
  people: PersonRow[];
  general?: GeneralView;
  /** Someone has comment access (shown with the "comments are coming soon" note). */
  commentNote: boolean;
  locked: boolean;
  // Invite
  query: string;
  candidates: InviteCandidate[];
  selected: InviteCandidate[];
  inviteLevel: PageAccess;
  /** Guests invited by email: when their access ends. */
  guestExpiry: ExpiryOption["key"];
  expiryOptions: ExpiryOption[];
  /** Shown while an email (future guest) is selected. */
  guestNote?: MessageDescriptor;
  inviting: boolean;
  publish: PublishView;
  error?: MessageDescriptor;
  notice?: MessageDescriptor;
};

const CANDIDATE_LIMIT = 8;
const NO_CAPS: SharingCapabilities = sharingCapabilities({ role: "member", restricted: false, settings: DEFAULT_SETTINGS });

function failureMessage(e: unknown): MessageDescriptor {
  if (e instanceof ApiFailure && e.code === "last_full_access") return msg("sharing.error.lastFullAccess");
  if (e instanceof ApiFailure && e.status === 409) return msg("sharing.error.alreadyMember");
  return msg("sharing.error.generic");
}

/** The node fields whose change means the dialog's state changed elsewhere. */
function signature(p: PageNode | undefined): string {
  if (p === undefined) return "";
  return [p.myAccess, p.generalAccess ?? "", p.sharedWith ?? 0, p.published === true, p.locked === true, p.visibility, p.parentId ?? ""].join("|");
}

export class PageShareViewModel extends ViewModel<PageShareState> {
  private readonly ws: WorkspaceViewModel;
  private readonly api: SharingApi;
  private readonly tree: PageTreeViewModel | undefined;
  private sharing: PageSharing | undefined;
  private seen = "";
  private loading: Promise<void> | undefined;
  /** A refresh was asked for while one was in flight (its answer may predate the change). */
  private again = false;
  /** After a sharing change seen in the tree: the optimistic node's updatedAt, until the server's echo replaces it. */
  private echoFrom: number | undefined;

  constructor(ws: WorkspaceViewModel, api: SharingApi, tree: PageTreeViewModel | undefined, pageId: string) {
    const now = ws.deps.clock.now();
    super({
      status: "loading",
      pageId,
      title: "",
      kind: "page",
      indicator: { icon: "lock", label: msg("sharing.indicator.private") },
      caps: NO_CAPS,
      people: [],
      commentNote: false,
      locked: false,
      query: "",
      candidates: [],
      selected: [],
      inviteLevel: "edit",
      guestExpiry: "never",
      expiryOptions: expiryOptions(now),
      inviting: false,
      publish: { available: false, on: false, expiry: "never", busy: false },
    });
    this.ws = ws;
    this.api = api;
    this.tree = tree;
    this.seen = signature(tree?.page(pageId));
    if (tree !== undefined) this.onDispose(tree.subscribe(() => this.onTree()));
    this.onDispose(ws.subscribe(() => this.derive()));
    void this.refresh();
  }

  private onTree(): void {
    const node = this.tree?.page(this.state.pageId);
    const sig = signature(node);
    if (sig !== this.seen) {
      this.seen = sig;
      this.echoFrom = node?.updatedAt;
      this.derive();
      void this.refresh();
      return;
    }
    // The server confirmed the change (same fields, its own updatedAt): fetch again, in case the first fetch raced it.
    if (this.echoFrom !== undefined && node !== undefined && node.updatedAt !== this.echoFrom) {
      this.echoFrom = undefined;
      void this.refresh();
    }
  }

  refresh(): Promise<void> {
    if (this.loading !== undefined) {
      this.again = true;
      return this.loading;
    }
    this.loading = (async () => {
      do {
        this.again = false;
        try {
          this.sharing = await this.api.pageSharing(this.ws.wsId, this.state.pageId);
          this.derive();
        } catch (e) {
          const next = { ...this.state, status: this.sharing === undefined ? ("error" as const) : ("ready" as const) };
          if (errorKind(e) !== "notFound") next.error = msg("sharing.error.generic");
          this.setState(next);
        }
      } while (this.again && !this.isDisposed);
      this.loading = undefined;
    })();
    return this.loading;
  }

  /** The last fetched state with the tree's newer section, when a move hasn't reached it yet. */
  private effective(): PageSharing | undefined {
    const s = this.sharing;
    const node = this.tree?.page(this.state.pageId);
    if (s === undefined || node === undefined || node.visibility === s.visibility) return s;
    if (node.visibility === "private" && s.generalSource !== "page") return { ...s, visibility: "private", general: "none", generalSource: "default" };
    return { ...s, visibility: node.visibility };
  }

  private me() {
    const cache = this.ws.state.cache;
    return cache?.members[cache.meId];
  }

  /** Everything shown, from the last PageSharing + the workspace cache. */
  private derive(): void {
    const s = this.effective();
    if (s === undefined) return;
    const cache = this.ws.state.cache;
    const me = this.me();
    const meId = this.ws.state.meId;
    const restricted = me === undefined ? true : isRestrictedSubject(me.role, me.access?.level === "restricted");
    const caps = sharingCapabilities({ access: s.myAccess, role: me?.role ?? "guest", restricted, settings: s.settings });
    const node = this.tree?.page(s.pageId);
    const workspaceName = cache?.workspace.name ?? "";
    const general: GeneralView = generalControl(s, caps, workspaceName);
    const blocked = s.myAccess !== "full" ? msg("sharing.publish.needsFull") : publishBlockedReason(s.settings, me?.role ?? "guest", restricted);
    const publish: PublishView = { available: caps.canPublish, on: s.link !== undefined, expiry: this.state.publish.expiry, busy: this.state.publish.busy };
    if (s.link !== undefined) publish.link = s.link;
    if (!caps.canPublish && blocked !== undefined) publish.blockedReason = blocked;
    const people = peopleRows(s, cache?.members ?? {}, meId, caps);
    const levels = caps.inviteLevels;
    const next: PageShareState = {
      ...this.state,
      status: "ready",
      title: node?.title ?? this.state.title,
      kind: node?.kind ?? this.state.kind,
      indicator: shareIndicator(s, meId),
      caps,
      people,
      general,
      commentNote: s.people.some((p) => p.access === "comment") || s.general === "comment",
      locked: s.locked,
      inviteLevel: levels.includes(this.state.inviteLevel) ? this.state.inviteLevel : (levels.find((l) => l === "edit") ?? levels[0] ?? "view"),
      candidates: this.candidates(this.state.query, caps),
      publish,
    };
    this.setState(next);
  }

  private candidates(query: string, caps: SharingCapabilities): InviteCandidate[] {
    const s = this.sharing;
    const cache = this.ws.state.cache;
    if (s === undefined || cache === undefined || !caps.canInvite) return [];
    const picked = new Set(this.state.selected.map((c) => c.key));
    return inviteCandidates(query, Object.values(cache.members), s.people, cache.meId, caps.canInviteGuests, CANDIDATE_LIMIT).filter((c) => !picked.has(c.key));
  }

  // ---------- Invite ----------

  setQuery(query: string): void {
    this.update({ query, candidates: this.candidates(query, this.state.caps) });
  }

  /** Adds a candidate to the invite list (people who already have access aren't selectable). */
  select(c: InviteCandidate): void {
    if (c.hasAccess || this.state.selected.some((x) => x.key === c.key)) return;
    const selected = [...this.state.selected, c];
    this.setState({ ...this.state, selected, query: "" });
    this.afterSelection();
  }

  unselect(key: string): void {
    this.setState({ ...this.state, selected: this.state.selected.filter((c) => c.key !== key) });
    this.afterSelection();
  }

  private afterSelection(): void {
    const email = this.state.selected.find((c) => c.kind === "email");
    const next: PageShareState = { ...this.state, candidates: this.candidates(this.state.query, this.state.caps) };
    if (email?.email !== undefined) next.guestNote = msg("sharing.invite.guestNote", { email: email.email });
    else delete next.guestNote;
    this.setState(next);
  }

  setInviteLevel(level: PageAccess): void {
    if (this.state.caps.inviteLevels.includes(level)) this.update({ inviteLevel: level });
  }

  setGuestExpiry(key: ExpiryOption["key"]): void {
    this.update({ guestExpiry: key });
  }

  /** Invites everyone selected at `inviteLevel` (members/agents get a share; emails become page-scoped guests). */
  async invite(): Promise<boolean> {
    const people = this.state.selected;
    if (people.length === 0 || this.state.inviting) return false;
    this.update({ inviting: true });
    const level = this.state.inviteLevel;
    const expiresAt = this.state.expiryOptions.find((o) => o.key === this.state.guestExpiry)?.expiresAt;
    try {
      for (const c of people) {
        if (c.kind === "email" && c.email !== undefined) {
          const req: InvitePageGuestRequest = { email: c.email, access: level, organization: organizationFromEmail(c.email) };
          if (expiresAt !== undefined) req.expiresAt = expiresAt;
          this.sharing = (await this.api.inviteGuest(this.ws.wsId, this.state.pageId, req)).sharing;
        } else if (c.userId !== undefined) {
          this.sharing = await this.api.setPermission(this.ws.wsId, this.state.pageId, c.userId, level);
        }
        this.setState({ ...this.state, selected: this.state.selected.filter((x) => x.key !== c.key) });
      }
      const s = { ...this.state, inviting: false, notice: msg("sharing.invite.sent") };
      delete s.guestNote;
      delete s.error;
      this.setState(s);
      this.derive();
      return true;
    } catch (e) {
      this.update({ inviting: false, error: failureMessage(e) });
      this.derive();
      return false;
    }
  }

  // ---------- People ----------

  async setLevel(userId: string, access: PageAccess): Promise<void> {
    await this.mutate(() => this.api.setPermission(this.ws.wsId, this.state.pageId, userId, access));
  }

  async remove(userId: string): Promise<void> {
    await this.mutate(() => this.api.removePermission(this.ws.wsId, this.state.pageId, userId));
  }

  // ---------- General access ----------

  /**
   * "Everyone at <workspace>" at a level, or only people invited. A private page shared with the workspace stays in
   * its owner's Private section (others find it under "Shared with me").
   */
  async setGeneral(value: GeneralAccess): Promise<void> {
    if (this.sharing === undefined || !this.state.general?.editable || value === this.state.general.value) return;
    await this.mutate(() => this.api.setGeneralAccess(this.ws.wsId, this.state.pageId, value));
  }

  // ---------- Publish ----------

  async setPublished(on: boolean): Promise<void> {
    // One publish / unpublish at a time (a second toggle while the first is in flight would race it).
    if (!this.state.publish.available || this.state.publish.busy) return;
    const expiresAt = this.state.expiryOptions.find((o) => o.key === this.state.publish.expiry)?.expiresAt;
    const req: PublishPageRequest = {};
    if (expiresAt !== undefined) req.expiresAt = expiresAt;
    await this.mutate(() => (on ? this.api.publish(this.ws.wsId, this.state.pageId, req) : this.api.unpublish(this.ws.wsId, this.state.pageId)), true);
  }

  async setPublishOption(key: "allowIndexing" | "allowDuplicate" | "includeSubpages", value: boolean): Promise<void> {
    if (!this.state.publish.available || !this.state.publish.on) return;
    await this.mutate(() => this.api.publish(this.ws.wsId, this.state.pageId, { [key]: value }), true);
  }

  async setLinkExpiry(key: ExpiryOption["key"]): Promise<void> {
    this.update({ publish: { ...this.state.publish, expiry: key } });
    if (!this.state.publish.on) return;
    const expiresAt = this.state.expiryOptions.find((o) => o.key === key)?.expiresAt ?? null;
    await this.mutate(() => this.api.publish(this.ws.wsId, this.state.pageId, { expiresAt }), true);
  }

  // ---------- Notices ----------

  /** The UI copied a link (internal or public). */
  copied(): void {
    this.update({ notice: msg("sharing.copied") });
  }

  dismissNotice(): void {
    if (this.state.notice === undefined && this.state.error === undefined) return;
    const s = { ...this.state };
    delete s.notice;
    delete s.error;
    this.setState(s);
  }

  private async mutate(fn: () => Promise<PageSharing>, publishing = false): Promise<void> {
    if (publishing) this.update({ publish: { ...this.state.publish, busy: true } });
    try {
      this.sharing = await fn();
      const s = { ...this.state, publish: { ...this.state.publish, busy: false } };
      delete s.error;
      this.setState(s);
      this.derive();
    } catch (e) {
      this.update({ error: failureMessage(e), publish: { ...this.state.publish, busy: false } });
    }
  }
}
